import React, { useEffect, useRef } from 'react'
import { Dialog, DialogContent, IconButton } from '@mui/material'
import { Close } from '@mui/icons-material'
import Hls from 'hls.js'

interface Props {
  url: string
  open: boolean
  setOpen: (op: boolean) => void
}

const PlayVideo: React.FC<Props> = ({ url, open, setOpen }: Props) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const hlsRef = useRef<Hls | null>(null)

  const destroyHls = () => {
    if (hlsRef.current) {
      hlsRef.current.destroy()
      hlsRef.current = null
    }
  }

  useEffect(() => {
    if (!open || !url) return

    const timer = setTimeout(() => {
      const video = videoRef.current
      if (!video) return

      destroyHls()

      const isHls = url.includes('.m3u8')

      if (isHls) {
        if (Hls.isSupported()) {
          const hls = new Hls({
            enableWorker: true,
            lowLatencyMode: false
          })
          hlsRef.current = hls
          hls.loadSource(url)
          hls.attachMedia(video)
          hls.on(Hls.Events.ERROR, (_event, data) => {
            if (data.fatal) {
              console.error('HLS fatal error:', data.type, data.details)
            }
          })
        } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
          video.src = url
          video.load()
        }
      } else {
        // MP4 u otro formato nativo
        video.src = url
        video.load()
      }
    }, 100)

    return () => {
      clearTimeout(timer)
      destroyHls()
    }
  }, [open, url])

  const handleClose = () => {
    destroyHls()
    if (videoRef.current) {
      videoRef.current.pause()
      videoRef.current.removeAttribute('src')
      videoRef.current.load()
    }
    setOpen(false)
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { backgroundColor: '#1e1e1e', borderRadius: 2 }
      }}
    >
      <IconButton
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: '#fff',
          zIndex: 1,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.7)' }
        }}
      >
        <Close />
      </IconButton>
      <DialogContent sx={{ padding: 0, backgroundColor: '#000' }}>
        <video
          ref={videoRef}
          controls
          playsInline
          preload="auto"
          style={{
            width: '100%',
            height: '80vh',
            display: 'block',
            backgroundColor: '#000'
          }}
        />
      </DialogContent>
    </Dialog>
  )
}

export default PlayVideo
